const PROPERTY_SEARCH_DISQUALIFIERS = [
  "bhk", "bedroom", "bathroom", "sqft", "sq ft", "square feet",
  "apartment", "villa", "flat", "plot", "penthouse", "studio", "duplex", "farmhouse",
  "ready to move", "ready-to-move", "under construction", "new launch", "pre launch",
  "resale", "rera", "furnished", "unfurnished", "semi-furnished",
  "gated community", "luxury", "affordable", "premium",
  "budget", "crore", "lakh", "lakhs", " cr ", "under ₹", "below ₹",
  "amenities", "swimming pool", "gym", "clubhouse",
  "vastu", "possession", "listing", "developer",
];

const NAVIGATION_KEYWORDS = [
  "distance", "route", "directions", "navigate", "navigation",
  "how far", "travel time", "driving time", "drive time",
  "time to reach", "km from", "kilometers from", "miles from",
  "get directions", "show route", "find route",
];

// FIX: Added 1st, 2nd, and above property tokens
const THIS_PROPERTY_TOKENS = [
  "this property", "the property", "this place", "this home",
  "this flat", "this apartment", "this villa", "this location",
  "above property", "above project", "the above",
  "1st property", "1 st property", "first property",
  "2nd property", "2 nd property", "second property"
];

const ROUTE_PATTERNS = [
  /what\s+is\s+(?:the\s+)?distance\s+(?:between\s+)?(.+?)\s+(?:to|and|from)\s+(.+)/i,
  /distance\s+(?:this|the)\s+(?:property|place|home|flat|apartment|villa)\s+(?:to|from)\s+(.+)/i,
  /distance\s+between\s+(.+?)\s+(?:and|to)\s+(.+)/i,
  /distance\s+from\s+(.+?)\s+to\s+(.+)/i,
  /(?:route|directions?|navigate|navigation|path)\s+from\s+(.+?)\s+to\s+(.+)/i,
  /\bhow\s+far\s+(?:is\s+)?(.+?)\s+from\s+(.+)/i,
  /^(.+?)\s+to\s+(.+?)\s+(?:distance|route|directions?|km|kilometers?|miles?)\s*\??$/i,
  /(?:travel\s+time|drive|driving)\s+from\s+(.+?)\s+to\s+(.+)/i,
  /show\s+(?:route|directions?|path)\s+(?:from\s+)?(.+?)\s+to\s+(.+)/i,
  /(?:find|get)\s+(?:me\s+)?(?:route|directions?)\s+(?:from\s+)?(.+?)\s+to\s+(.+)/i,
  /time\s+to\s+reach\s+(.+?)\s+from\s+(.+)/i,
];

function isPropertySearch(text) {
  const lower = text.toLowerCase();
  return PROPERTY_SEARCH_DISQUALIFIERS.some(kw => lower.includes(kw));
}

function hasNavigationKeyword(text) {
  const lower = text.toLowerCase();
  return NAVIGATION_KEYWORDS.some(kw => lower.includes(kw));
}

function isValidLocation(str) {
  if (!str || str.length < 2) return false;
  const lower = str.toLowerCase().trim();

  // FIX: Unconditionally allow our contextual tokens so they pass the map interceptor
  if (THIS_PROPERTY_TOKENS.some(t => lower.includes(t))) return true;

  const badTerms = [
    "bhk", "bedroom", "apartment", "villa", "flat", "plot", "studio",
    "ready", "move", "crore", "lakh", "sqft", "furnished", "rera",
    "luxury", "affordable", "gated", "community", "amenities",
    "find", "show", "looking", "want", "need", "suggest", "budget",
    "property", "above", "listed property",
  ];
  if (badTerms.some(t => lower.includes(t))) return false;

  // Increased word limit slightly to allow longer address strings
  if (str.split(/\s+/).length > 8) return false;

  return true;
}

export function detectRouteIntent(text) {
  if (!text) return null;
  const cleaned = text.trim();

  if (isPropertySearch(cleaned)) return null;
  if (!hasNavigationKeyword(cleaned)) return null;

  for (const pattern of ROUTE_PATTERNS) {
    const match = cleaned.match(pattern);
    if (!match) continue;

    if (match[1] && !match[2]) {
      const destination = match[1].trim().replace(/[?!.,]+$/, "").trim();
      if (isValidLocation(destination)) {
        return { origin: "__PROPERTY__", destination, usePropAsOrigin: true };
      }
      continue;
    }

    const origin      = match[1].trim().replace(/[?!.,]+$/, "").trim();
    const destination = match[2].trim().replace(/[?!.,]+$/, "").trim();

    if (!isValidLocation(origin) || !isValidLocation(destination)) continue;
    if (origin.toLowerCase() === destination.toLowerCase()) continue;

    const isPropertyOrigin = THIS_PROPERTY_TOKENS.some(t =>
        origin.toLowerCase().includes(t.toLowerCase())
    );

    return {
      origin:          isPropertyOrigin ? "__PROPERTY__" : origin,
      destination,
      usePropAsOrigin: isPropertyOrigin,
    };
  }

  if (hasNavigationKeyword(cleaned)) {
    const simpleMatch = cleaned.match(/^(.+?)\s+to\s+(.+?)\s*\??$/i);
    if (simpleMatch) {
      const a = simpleMatch[1].trim();
      const b = simpleMatch[2].trim();
      if (isValidLocation(a) && isValidLocation(b)) {
        const isPropertyOrigin = THIS_PROPERTY_TOKENS.some(t => a.toLowerCase().includes(t));
        return {
          origin:          isPropertyOrigin ? "__PROPERTY__" : a,
          destination:     b,
          usePropAsOrigin: isPropertyOrigin,
        };
      }
    }
  }

  return null;
}

const PLACE_TYPE_MAP = {
  "school":      { type: "school",            label: "Schools"      },
  "schools":     { type: "school",            label: "Schools"      },
  "hospital":    { type: "hospital",          label: "Hospitals"    },
  "hospitals":   { type: "hospital",          label: "Hospitals"    },
  "restaurant":  { type: "restaurant",        label: "Restaurants"  },
  "restaurants": { type: "restaurant",        label: "Restaurants"  },
  "hotel":       { type: "lodging",           label: "Hotels"       },
  "hotels":      { type: "lodging",           label: "Hotels"       },
  "gym":         { type: "gym",               label: "Gyms"         },
  "gyms":        { type: "gym",               label: "Gyms"         },
  "mall":        { type: "shopping_mall",     label: "Malls"        },
  "malls":       { type: "shopping_mall",     label: "Malls"        },
  "temple":      { type: "place_of_worship",  label: "Temples"      },
  "temples":     { type: "place_of_worship",  label: "Temples"      },
  "pharmacy":    { type: "pharmacy",          label: "Pharmacies"   },
  "pharmacies":  { type: "pharmacy",          label: "Pharmacies"   },
  "park":        { type: "park",              label: "Parks"        },
  "parks":       { type: "park",              label: "Parks"        },
  "atm":         { type: "atm",               label: "ATMs"         },
  "bank":        { type: "bank",              label: "Banks"        },
  "banks":       { type: "bank",              label: "Banks"        },
};

export function detectNearbyIntent(text) {
  if (!text) return null;
  const lower = text.toLowerCase().trim();

  const hasNearbyLanguage = /close\s+by|nearby|near\s+me|near\s+\w|around\s+me|hospitals|schools|gyms|restaurants|malls|temples|pharmacies|parks/.test(lower);
  if (!hasNearbyLanguage) return null;

  let placeInfo = null;
  for (const [word, info] of Object.entries(PLACE_TYPE_MAP)) {
    if (lower.includes(word)) { placeInfo = info; break; }
  }
  if (!placeInfo) return null;

  if (/close\s+by|nearby|near\s+me|around\s+me|from\s+here/.test(lower)) {
    return { ...placeInfo, useCurrentLocation: true, location: null };
  }

  const locMatch = lower.match(/(?:near|in|around|close\s+to)\s+([a-z][a-z\s]{1,30})(?:\?|$)/);
  if (locMatch?.[1]?.trim().length > 2) {
    return { ...placeInfo, useCurrentLocation: false, location: locMatch[1].trim() };
  }

  return { ...placeInfo, useCurrentLocation: true, location: null };
}

export function resolvePropertyReference(text, propertyLocation) {
  if (!text || !propertyLocation) return text;
  let resolved = text;
  THIS_PROPERTY_TOKENS.forEach(token => {
    resolved = resolved.replace(new RegExp(token, "gi"), propertyLocation);
  });
  return resolved;
}